import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TextsContext = createContext({});

export const useTexts = () => useContext(TextsContext);

// Available languages - using text codes instead of emoji flags to avoid blob CSP issues
export const LANGUAGES = [
  { code: 'de', name: 'Deutsch', flag: 'DE' },
  { code: 'en', name: 'English', flag: 'EN' },
  { code: 'fr', name: 'Français', flag: 'FR' },
  { code: 'es', name: 'Español', flag: 'ES' },
  { code: 'it', name: 'Italiano', flag: 'IT' },
  { code: 'pt', name: 'Português', flag: 'PT' },
  { code: 'ru', name: 'Русский', flag: 'RU' },
  { code: 'zh', name: '中文', flag: 'ZH' },
  { code: 'hi', name: 'हिन्दी', flag: 'HI' },
];

// Cache key for localStorage
const TEXTS_CACHE_KEY = 'place_of_lights_texts';
const TEXTS_CACHE_VERSION = 'v1';

// Get cached texts from localStorage
const getCachedTexts = (lang) => {
  try {
    const cached = localStorage.getItem(`${TEXTS_CACHE_KEY}_${lang}`);
    if (cached) {
      const parsed = JSON.parse(cached);
      // Check if cache is not too old (24 hours)
      if (parsed.version === TEXTS_CACHE_VERSION && 
          parsed.timestamp && 
          Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
        return parsed.texts;
      }
    }
  } catch (e) {
    console.warn('Error reading texts cache:', e);
  }
  return null;
};

// Save texts to localStorage cache
const setCachedTexts = (lang, texts) => {
  try {
    localStorage.setItem(`${TEXTS_CACHE_KEY}_${lang}`, JSON.stringify({
      version: TEXTS_CACHE_VERSION,
      timestamp: Date.now(),
      texts
    }));
  } catch (e) {
    console.warn('Error saving texts cache:', e);
  }
};

export const TextsProvider = ({ children }) => {
  // Get initial language from localStorage or browser
  const getInitialLanguage = () => {
    const stored = localStorage.getItem('language');
    if (stored && LANGUAGES.find(l => l.code === stored)) {
      return stored;
    }
    // Try browser language
    const browserLang = navigator.language?.split('-')[0];
    if (LANGUAGES.find(l => l.code === browserLang)) {
      return browserLang;
    }
    return 'de'; // Default
  };

  const [language, setLanguageState] = useState(getInitialLanguage);
  // Initialize with cached texts immediately (no FOUC)
  const [texts, setTexts] = useState(() => getCachedTexts(getInitialLanguage()) || {});
  const [loading, setLoading] = useState(false);

  const setLanguage = useCallback((lang) => {
    localStorage.setItem('language', lang);
    // Immediately load cached texts for new language (prevents flash)
    const cachedTexts = getCachedTexts(lang);
    if (cachedTexts) {
      setTexts(cachedTexts);
    }
    setLanguageState(lang);
  }, []);

  const loadTexts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/public/texts/${language}`);
      const newTexts = response.data;
      setTexts(newTexts);
      // Cache for next visit
      setCachedTexts(language, newTexts);
    } catch (error) {
      console.log('Using cached/default texts');
      // On error, try to use cached texts
      const cachedTexts = getCachedTexts(language);
      if (cachedTexts && Object.keys(cachedTexts).length > 0) {
        setTexts(cachedTexts);
      }
    } finally {
      setLoading(false);
    }
  }, [language]);

  // Load texts on mount and when language changes
  useEffect(() => {
    loadTexts();
  }, [loadTexts]);

  // Helper function to get text with fallback
  const t = useCallback((key, fallback) => {
    return texts[key] || fallback;
  }, [texts]);

  const currentLanguage = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];

  return (
    <TextsContext.Provider value={{ 
      texts, 
      t, 
      loading, 
      reload: loadTexts,
      language,
      setLanguage,
      currentLanguage,
      languages: LANGUAGES
    }}>
      {children}
    </TextsContext.Provider>
  );
};
